import { CustomChip } from '@app/components/CustomChip';
import { webApi } from '@mi-front/api-clients';
import { defineMessages, useIntl } from 'react-intl';
import { QUALITY_ICONS, QualityStatusColors } from '../consts';
import { useQualityStatusColors } from '../useQualityStatusColors';
import { TimelineTooltipPayload } from '../lib/timeline-tooltip-types';

/** i18n for gap vs audio issue labels (shared with timeline tooltip). */
const streamAvailabilityEventTypeMessages = defineMessages({
  gapTitle: {
    id: 'logger.reports.streamQuality.availability.tooltip.gap',
    defaultMessage: 'Sin emisión',
  },
  issueKindSilent: {
    id: 'logger.reports.streamQuality.availability.tooltip.issueKind.silent',
    defaultMessage: 'Silencio',
  },
  issueKindPoorQuality: {
    id: 'logger.reports.streamQuality.availability.tooltip.issueKind.poorQuality',
    defaultMessage: 'Calidad deficiente',
  },
});

function getTitleMessage(payload: TimelineTooltipPayload) {
  if (payload.type === 'gap') {
    return streamAvailabilityEventTypeMessages.gapTitle;
  }
  if (payload.issue.kind === webApi.AudioQualityIssueDtoKindEnum.silent) {
    return streamAvailabilityEventTypeMessages.issueKindSilent;
  }
  return streamAvailabilityEventTypeMessages.issueKindPoorQuality;
}

/**
 * Accent hex for the event chip; must stay aligned with timeline bar colors in `../consts`.
 */
function getEventChipAccent(
  payload: TimelineTooltipPayload,
  statusColors: QualityStatusColors,
): string {
  if (payload.type === 'gap') {
    return statusColors.offline;
  }

  if (payload.issue.kind === webApi.AudioQualityIssueDtoKindEnum.silent) {
    return statusColors.issueSilent;
  }

  return statusColors.issuePoorQuality;
}

function getEventChipIcon(payload: TimelineTooltipPayload) {
  if (payload.type === 'gap') {
    return QUALITY_ICONS.gap;
  }
  if (payload.issue.kind === webApi.AudioQualityIssueDtoKindEnum.silent) {
    return QUALITY_ICONS.audioSilent;
  }
  return QUALITY_ICONS.audioPoorQuality;
}

export type EventTypeChipProps = {
  payload: TimelineTooltipPayload;
  /** When false, omits bottom margin (e.g. table cells). Defaults to true for tooltip layout. */
  withBottomMargin?: boolean;
};

/**
 * Colored chip for segment type (stream gap vs audio issue kind); uses the same hex accents as the timeline.
 */
export function EventTypeChip({ payload, withBottomMargin = true }: EventTypeChipProps) {
  const { $t } = useIntl();
  const { statusColors } = useQualityStatusColors();
  const Icon = getEventChipIcon(payload);
  const accent = getEventChipAccent(payload, statusColors);

  return (
    <CustomChip
      size="small"
      variant="filled"
      chipColor={accent}
      filledLabelColor={
        accent === statusColors.issueSilent || accent === statusColors.offline ? '#fff' : undefined
      }
      icon={<Icon />}
      label={$t(getTitleMessage(payload))}
      sx={{
        ...(withBottomMargin ? { mb: 1 } : {}),
        '& .MuiChip-label': { typography: 'caption' },
        '& .MuiChip-icon': { color: 'currentColor' },
      }}
      useDarken={false}
    />
  );
}
